/**
 * Перенос `presentation/providers/chat_controller/chat_controller_provider.dart`.
 *
 * `InheritedWidget` заменён обычным контекстом React. Различия
 * `ChatController.of(context, createDependency: true/false)` здесь нет:
 * во Flutter `dependOnInheritedWidgetOfExactType` подписывал виджет на
 * пересборку провайдера, а `getInheritedWidgetOfExactType` — нет. В React
 * значение контекста — сам контроллер, объект неизменный на всю жизнь
 * экрана, поэтому подписка на него всё равно ничего не перерисовывает;
 * перерисовку даёт `useValueNotifier(controller)`, то есть подписка на
 * состояние. Одного хука достаточно.
 */

import { createContext, useContext } from 'react';
import type { ReactElement, ReactNode } from 'react';

import type { ChatController } from './chatController';

const ChatControllerContext = createContext<ChatController | null>(null);

export interface ChatControllerProviderProps {
  readonly controller: ChatController;
  readonly children: ReactNode;
}

export function ChatControllerProvider({
  controller,
  children,
}: ChatControllerProviderProps): ReactElement {
  return (
    <ChatControllerContext.Provider value={controller}>
      {children}
    </ChatControllerContext.Provider>
  );
}

/**
 * `ChatController.of(context)` — в Dart возвращал непустое значение
 * (`...!`) и падал, если провайдера нет. Здесь то же самое, только с
 * понятным текстом.
 */
export function useChatController(): ChatController {
  const controller = useContext(ChatControllerContext);
  if (controller === null) {
    throw new Error('useChatController: нет ChatControllerProvider выше по дереву');
  }
  return controller;
}
